import React, { useContext, useEffect, useMemo, useRef } from 'react';
import mapboxgl from 'mapbox-gl';

import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';

import 'mapbox-gl/dist/mapbox-gl.css';
import './mapBoxMap.css';

const DEFAULT_CENTER: [number, number] = [-98.5795, 39.8282]; // lng, lat
const DEFAULT_ZOOM = 10.0;

const isValidCenter = (center: [number, number]): boolean => {
  if (center[0] === 0 && center[1] === 0) {
    return false;
  }

  return (
    center[1] >= -90 && center[1] <= 90 && center[0] >= -180 && center[0] <= 180
  );
};

const DEFAULT_MAP_STYLE = '';

const DRAGGABLE_CURSOR = 'default';
const DRAGGING_CURSOR = 'move';
const MARKER_SIZE = 32;

interface Marker {
  lng: number;
  lat: number;
  id?: string;
  iconUrl?: string;
}

interface Props {
  center?: [number, number];
  zoom?: number;
  bounds?: [[number, number], [number, number]];
  markers: Marker[];
  onMapClick?: (lngLat: mapboxgl.LngLat) => void;
  onBoundsChanged?: (bounds: mapboxgl.LngLatBounds) => void;
  style?: React.CSSProperties;
  mapStyle?: string;
  markerSize?: number;
  height?: number | string;
}

const MAPBOX_TOKEN =
  'pk.eyJ1Ijoic2hhbm5vbi1zbWl0aCIsImEiOiJjbWRlbGIyaDYwM3J6MmtwcWM5NGo2czZjIn0.-zC6hkZ7uQmf3E1nDr6zLg';

const MapBoxMap: React.FC<Props> = ({
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  bounds,
  markers = [],
  onMapClick,
  onBoundsChanged,
  style,
  mapStyle = DEFAULT_MAP_STYLE,
  markerSize = MARKER_SIZE,
  height = 400,
}) => {
  const { preferredTheme } = useContext(UserProfileContext);

  const useMapStyle = useMemo(() => {
    if (isNotNull(mapStyle) && mapStyle !== '') {
      return mapStyle;
    }

    if (preferredTheme === 'dark') {
      return 'mapbox://styles/mapbox/navigation-night-v1';
      // Options:
      //  navigation-night-v1
      //  dark-v11
    }

    return 'mapbox://styles/mapbox/streets-v11';
  }, [preferredTheme, mapStyle]);

  const mapContainerRef = useRef<HTMLDivElement>();
  const mapRef = useRef<mapboxgl.Map>();
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  // Initialize map only once
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) {
      return;
    }

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      center: isValidCenter(center) ? center : DEFAULT_CENTER,
      zoom,
      style: useMapStyle,
    });

    // Set custom cursor styles
    map.getCanvas().style.cursor = DRAGGABLE_CURSOR;

    map.on('dragstart', () => {
      map.getCanvas().style.cursor = DRAGGING_CURSOR;
    });

    map.on('dragend', () => {
      map.getCanvas().style.cursor = DRAGGABLE_CURSOR;
    });

    mapRef.current = map;

    map.on('click', (event) => {
      onMapClick?.(event.lngLat);
    });

    map.on('moveend', () => {
      onBoundsChanged?.(map.getBounds());
    });
    // eslint-disable-next-line consistent-return
    return () => {
      markersRef.current.forEach((marker) => marker.remove());

      markersRef.current = [];
      if (mapRef.current) {
        mapRef.current.remove();

        mapRef.current = null;
      }
    };
  }, []);

  // Update map style without recreating the map (preserves zoom/position)
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setStyle(useMapStyle);
    }
  }, [useMapStyle]);

  // Update center/zoom when props change
  useEffect(() => {
    if (!mapRef.current) {
      return;
    }
    if (bounds) {
      mapRef.current.fitBounds(bounds, { padding: 50 });
    } else if (isValidCenter(center)) {
      mapRef.current.setCenter(center);

      mapRef.current.setZoom(zoom);
    }
  }, [center, zoom, bounds, useMapStyle]);

  // Manage markers, supporting custom icons
  useEffect(() => {
    if (!mapRef.current) {
      return;
    }
    // Remove old markers
    markersRef.current.forEach((marker) => marker.remove());

    markersRef.current = [];

    markersRef.current = markers
      .filter((marker) => isValidCenter([marker.lng, marker.lat]))
      .map((markerInfo) => {
        const markerOptions: mapboxgl.MarkerOptions = {};

        if (markerInfo.iconUrl) {
          const el = document.createElement('div');

          el.style.backgroundImage = `url(${markerInfo.iconUrl})`;

          el.style.width = `${markerSize}px`;

          el.style.height = `${markerSize}px`;

          el.style.backgroundSize = 'contain';

          el.style.backgroundRepeat = 'no-repeat';

          el.style.display = 'block';

          el.style.transform = 'translate(-50%, -100%)';

          markerOptions.element = el;
        }

        return new mapboxgl.Marker(markerOptions)
          .setLngLat([Number(markerInfo.lng), Number(markerInfo.lat)])
          .addTo(mapRef.current!);
      });
  }, [markerSize, markers]);

  return (
    <div className="map-container" style={{ height }}>
      <div
        ref={mapContainerRef}
        style={{
          height: '100%',
          ...style,
        }}
      />
    </div>
  );
};

export default MapBoxMap;
